import React, { useState, useEffect, useRef } from 'react';
import { Loader2, Plus, X, Copy, Send, Download, ChevronDown, ChevronUp, Flame, ThumbsUp, ThumbsDown, Database } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { toast } from 'sonner';
import apiClient from '@/lib/apiClient';
import { copyToClipboard, escapeCsvCell, validateCitations, estimateTestCount } from '@/lib/dashboardUtils';

const TEST_CASES = [
  'Was sind die Öffnungszeiten des Rathauses?',
  'Wie kann ich einen Bewohnerparkausweis beantragen?',
  'Wo finde ich Formulare für die Gemeinde?',
  'Wie kann ich das Rathaus kontaktieren?',
  'Was steht auf der Startseite von Vaterstetten?',
  'Wann ist der Kinderfasching?',
  'Wo kann ich einen Reisepass beantragen?',
  'Wie melde ich meinen Wohnsitz an?',
  'Wo finde ich die Abfallkalender?',
  'Wie erhalte ich einen Führungszeugnis?'
];

const INTENT_CATEGORIES = [
  { value: 'opening_hours', label: 'Öffnungszeiten' },
  { value: 'contact_finder', label: 'Kontakt finden' },
  { value: 'service_info', label: 'Leistungsinfos' },
  { value: 'service_application', label: 'Leistungsantrag' },
  { value: 'waste_logistics', label: 'Abfall & Entsorgung' },
  { value: 'appointment', label: 'Terminvereinbarung' },
  { value: 'event_future', label: 'Veranstaltungen' },
  { value: 'payments', label: 'Zahlungen & Gebühren' },
  { value: 'defect_reporting', label: 'Mängel melden' },
  { value: 'lost_found', label: 'Fundbüro' },
  { value: 'clarification', label: 'Klärung' },
  { value: 'abuse', label: 'Missbrauch' }
];

const SUITE_TAGS = [
  { value: 'P0_core', label: 'Nur P0 Core Tests' },
  { value: 'redteam', label: 'Redteam Tests' }
];

const SMOKE_TEST_QUERIES = [
  { id: 'smoke_oh_001', name: 'Opening Hours', question: 'Wie sind die Öffnungszeiten vom Rathaus?', expectedCanonicalPaths: ['/oeffnungszeiten', '/kontakt', '/rathaus/oeffnungszeiten'], forbiddenPaths: ['/aktuelles', '/news', '/veranstaltungen', '/events'] },
  { id: 'smoke_service_001', name: 'Service Question (Reisepass)', question: 'Ich brauche einen Reisepass – was muss ich mitbringen?', expectedCanonicalPaths: ['/leistungen', '/buergerservice'], forbiddenPaths: ['/aktuelles', '/news', '/veranstaltungen', '/events'] },
  { id: 'smoke_service_002', name: 'Service Question (Wohnsitz)', question: 'Wie melde ich meinen Wohnsitz um?', expectedCanonicalPaths: ['/leistungen', '/buergerservice'], forbiddenPaths: ['/aktuelles', '/news', '/veranstaltungen', '/events'] },
  { id: 'smoke_contact_001', name: 'Contact Information', question: 'Wie kann ich das Rathaus kontaktieren?', expectedCanonicalPaths: ['/kontakt', '/rathaus'], forbiddenPaths: ['/aktuelles', '/news', '/veranstaltungen', '/events'] },
  { id: 'smoke_waste_001', name: 'Waste Logistics', question: 'Wo finde ich die Abfallkalender?', expectedCanonicalPaths: ['/abfall', '/entsorgung'], forbiddenPaths: ['/aktuelles', '/news', '/veranstaltungen', '/events'] }
];

export default function TestumgebungTab({ isDark, userInfo, selectedMunicipalityId, municipalitiesList = [], initialTestInput = '' }) {
  const [customTestCases, setCustomTestCases] = useState(() => {
    try { return JSON.parse(localStorage.getItem('stadtki_custom_test_cases') || '[]'); } catch { return []; }
  });
  const [newTestCaseInput, setNewTestCaseInput] = useState('');
  const [testEnvMessages, setTestEnvMessages] = useState([]);
  const [testEnvInput, setTestEnvInput] = useState('');
  const [testEnvLoading, setTestEnvLoading] = useState(false);
  const [lastResponseJson, setLastResponseJson] = useState('');
  const [lastDebugResponse, setLastDebugResponse] = useState(null);
  const [lastQuestionForFeedback, setLastQuestionForFeedback] = useState('');
  const [feedbackHelpful, setFeedbackHelpful] = useState(null);
  const [feedbackReason, setFeedbackReason] = useState('');
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackSending, setFeedbackSending] = useState(false);

  const [testSuiteJobId, setTestSuiteJobId] = useState(null);
  const [testSuiteStatus, setTestSuiteStatus] = useState(null);
  const [testSuiteRunning, setTestSuiteRunning] = useState(false);
  const [testSuiteForm, setTestSuiteForm] = useState({
    suite: 'full', intent_tags: [], suite_tags: [], include_redteam: true, include_dialogues: false, limit: null
  });
  const [testSuiteReports, setTestSuiteReports] = useState([]);
  const testSuitePollIntervalRef = useRef(null);

  const [smokeTestResults, setSmokeTestResults] = useState(null);
  const [smokeTestRunning, setSmokeTestRunning] = useState(false);
  const [smokeTestExpanded, setSmokeTestExpanded] = useState({});

  // Accept initial test input from Monitoring tab
  useEffect(() => {
    if (initialTestInput) setTestEnvInput(initialTestInput);
  }, [initialTestInput]);

  useEffect(() => {
    setTestEnvMessages([]);
    setLastResponseJson('');
    setSmokeTestResults([]);
  }, [selectedMunicipalityId]);

  const testEnvCanSend = userInfo?.role !== 'ProviderAdmin' || !!selectedMunicipalityId;

  const addCustomTestCase = (question) => {
    const q = question.trim();
    if (!q) return;
    setCustomTestCases(prev => {
      const next = [...prev, q];
      localStorage.setItem('stadtki_custom_test_cases', JSON.stringify(next));
      return next;
    });
    setNewTestCaseInput('');
  };

  const removeCustomTestCase = (index) => {
    setCustomTestCases(prev => {
      const next = prev.filter((_, i) => i !== index);
      localStorage.setItem('stadtki_custom_test_cases', JSON.stringify(next));
      return next;
    });
  };

  const sendTestMessage = async (messageText) => {
    if (!messageText?.trim() || testEnvLoading || !testEnvCanSend) return;
    const question = messageText.trim();
    setTestEnvMessages(prev => [...prev, { role: 'user', content: question }]);
    setTestEnvInput('');
    setTestEnvLoading(true);
    setLastResponseJson('');
    setLastDebugResponse(null);
    setLastQuestionForFeedback(question);
    setFeedbackHelpful(null);
    setFeedbackReason('');
    setFeedbackComment('');
    const payload = { message: question, conversation_id: null };
    if (userInfo?.role === 'ProviderAdmin' && selectedMunicipalityId) payload.municipality_id = selectedMunicipalityId;
    try {
      const res = await apiClient.post('/ask_debug', payload);
      let jsonStr = '';
      try { jsonStr = JSON.stringify(res?.data ?? {}, null, 2); } catch (_) { jsonStr = JSON.stringify({ error: 'Response could not be serialized' }, null, 2); }
      setLastResponseJson(jsonStr);
      setLastDebugResponse(res?.data ?? null);
      const answerText = typeof res?.data?.answer === 'string' ? res.data.answer : String(res?.data?.answer ?? '');
      setTestEnvMessages(prev => [...prev, { role: 'assistant', content: answerText }]);
    } catch (e) {
      const errMsg = e.response?.data?.detail || e.message || 'Fehler';
      setLastResponseJson(JSON.stringify({ error: errMsg }, null, 2));
      setLastDebugResponse(null);
      setTestEnvMessages(prev => [...prev, { role: 'assistant', content: String(errMsg), error: true }]);
    } finally {
      setTestEnvLoading(false);
    }
  };

  const submitTestFeedback = async () => {
    if (feedbackHelpful === null || !lastQuestionForFeedback) return;
    setFeedbackSending(true);
    try {
      await apiClient.post('/feedback', {
        question: lastQuestionForFeedback,
        helpful: feedbackHelpful,
        reason: (feedbackReason && feedbackReason !== 'none') ? feedbackReason : null,
        municipality_id: userInfo?.municipality_id || null,
        widget_id: null
      });
      toast.success('Vielen Dank für Ihr Feedback!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Senden des Feedbacks');
    } finally {
      setFeedbackSending(false);
    }
  };

  // Test Suite functions
  const pollTestSuiteStatus = async (jobId) => {
    try {
      const response = await apiClient.get(`/provider/test-suite/${jobId}`);
      const status = response.data;
      setTestSuiteStatus(status);
      if (status.status === 'running') {
        setTestSuiteRunning(true);
      } else if (status.status === 'completed' || status.status === 'failed' || status.status === 'cancelled') {
        if (testSuitePollIntervalRef.current) { clearInterval(testSuitePollIntervalRef.current); testSuitePollIntervalRef.current = null; }
        const wasTracked = sessionStorage.getItem('testSuiteJobId') === jobId;
        setTestSuiteRunning(false);
        setTestSuiteJobId(null);
        sessionStorage.removeItem('testSuiteJobId');
        if (wasTracked) {
          if (status.status === 'completed') {
            const answerPassRate = status.results?.length > 0 ? (status.results.filter(r => r.answer_eval?.passed).length / status.results.length * 100).toFixed(1) : '0';
            const linkPassRate = status.results?.length > 0 ? (status.results.filter(r => r.link_eval?.passed).length / status.results.length * 100).toFixed(1) : '0';
            toast.success(`Test-Suite abgeschlossen: ${status.passed_tests}/${status.total_tests} Tests bestanden (Answer: ${answerPassRate}%, Links: ${linkPassRate}%)`);
            loadTestSuiteReports();
          } else if (status.status === 'failed') {
            toast.error(`Test-Suite fehlgeschlagen: ${status.error || 'Unbekannter Fehler'}`);
          }
        }
      }
    } catch (e) { console.error('Error polling test suite status:', e); }
  };

  const startTestSuite = async () => {
    if (!selectedMunicipalityId) { toast.error('Bitte wählen Sie eine Gemeinde aus'); return; }
    setTestSuiteRunning(true);
    try {
      const response = await apiClient.post('/provider/test-suite/run', {
        municipality_id: selectedMunicipalityId,
        suite: testSuiteForm.suite,
        intent_tags: testSuiteForm.intent_tags.length > 0 ? testSuiteForm.intent_tags : null,
        suite_tags: testSuiteForm.suite_tags.length > 0 ? testSuiteForm.suite_tags : null,
        include_redteam: testSuiteForm.include_redteam,
        include_dialogues: testSuiteForm.include_dialogues,
        limit: testSuiteForm.limit || null
      });
      setTestSuiteJobId(response.data.job_id);
      sessionStorage.setItem('testSuiteJobId', response.data.job_id);
      toast.success(`Test-Suite gestartet (Job: ${response.data.job_id.substring(0, 8)}...)`);
      if (testSuitePollIntervalRef.current) { clearInterval(testSuitePollIntervalRef.current); testSuitePollIntervalRef.current = null; }
      const interval = setInterval(() => { pollTestSuiteStatus(response.data.job_id); }, 2000);
      testSuitePollIntervalRef.current = interval;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Starten der Test-Suite');
      setTestSuiteRunning(false);
    }
  };

  const stopTestSuite = async () => {
    if (!testSuiteJobId) {
      if (testSuitePollIntervalRef.current) { clearInterval(testSuitePollIntervalRef.current); testSuitePollIntervalRef.current = null; }
      setTestSuiteRunning(false); setTestSuiteJobId(null); sessionStorage.removeItem('testSuiteJobId');
      toast.info('Test-Suite-Polling gestoppt'); return;
    }
    const jobIdToCancel = testSuiteJobId;
    try {
      await apiClient.post(`/provider/test-suite/${jobIdToCancel}/cancel`);
      if (testSuitePollIntervalRef.current) { clearInterval(testSuitePollIntervalRef.current); testSuitePollIntervalRef.current = null; }
      setTestSuiteRunning(false); setTestSuiteJobId(null); sessionStorage.removeItem('testSuiteJobId');
      toast.success('Test-Suite abgebrochen. Partielle Ergebnisse werden gespeichert.');
      try { const response = await apiClient.get(`/provider/test-suite/${jobIdToCancel}`); setTestSuiteStatus(response.data); loadTestSuiteReports(); } catch (e) {}
    } catch (e) {
      toast.error(`Fehler beim Abbrechen: ${e.response?.data?.detail || e.message || 'Unbekannter Fehler'}`);
      if (testSuitePollIntervalRef.current) { clearInterval(testSuitePollIntervalRef.current); testSuitePollIntervalRef.current = null; }
      setTestSuiteRunning(false); setTestSuiteJobId(null); sessionStorage.removeItem('testSuiteJobId');
    }
  };

  const loadTestSuiteReports = async () => {
    try { const response = await apiClient.get('/provider/test-suite/reports'); setTestSuiteReports(response.data || []); }
    catch (e) { if (e.response?.status !== 404) console.error('Error loading test suite reports:', e); setTestSuiteReports([]); }
  };

  const downloadReport = async (filename) => {
    try {
      const response = await apiClient.get(`/provider/test-suite/reports/${filename}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url; link.setAttribute('download', filename);
      document.body.appendChild(link); link.click(); link.remove();
      toast.success(`Report heruntergeladen: ${filename}`);
    } catch (e) { toast.error(`Fehler beim Herunterladen: ${e.response?.data?.detail || e.message}`); }
  };

  const exportTestSuiteResults = () => {
    if (!testSuiteStatus?.results) { toast.error('Keine Ergebnisse zum Exportieren'); return; }
    const rows = [];
    rows.push(['Base ID', 'Label', 'Intent', 'Tags', 'Base OK', 'Variant', 'Question', 'OK', 'Reasons', 'Got Intent', 'Fallback', 'Confidence', 'Selected Context N', 'Answer', 'Citations', 'Latency (ms)', 'Intent', 'Fallback Used']);
    testSuiteStatus.results.forEach(result => {
      result.variants.forEach(variant => {
        const answer = variant.answer || variant.response?.answer || '';
        const citations = variant.citations || variant.response?.citations || [];
        const citationsStr = citations.map(c => { const title = c.title || ''; const url = c.url || ''; return title ? `${title} (${url})` : url; }).filter(Boolean).join('\n');
        const latencyMs = variant.latency_ms || variant.response?.debug?.latency_ms || variant.response?.latency_ms || '';
        const intent = variant.got_intent || variant.intent || variant.response?.debug?.intent || '';
        const fallbackUsed = variant.fallback !== undefined ? (variant.fallback ? 'YES' : 'NO') : (variant.response?.debug?.used_fallback ? 'YES' : 'NO');
        rows.push([result.base_id, result.label, result.intent || '', result.tags.join(';'), result.base_ok ? 'YES' : 'NO', variant.variant, variant.question, variant.ok ? 'YES' : 'NO', variant.reasons.join(' | '), variant.got_intent || '', variant.fallback !== undefined ? (variant.fallback ? 'YES' : 'NO') : '', variant.confidence !== undefined ? String(variant.confidence) : '', variant.selected_context_n !== undefined ? String(variant.selected_context_n) : '', answer, citationsStr, latencyMs !== '' ? String(latencyMs) : '', intent, fallbackUsed]);
      });
    });
    const csv = rows.map(row => row.map(cell => escapeCsvCell(cell)).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a'); link.href = URL.createObjectURL(blob);
    link.download = `test-suite-results-${testSuiteJobId?.substring(0, 8) || 'export'}.csv`; link.click();
  };

  const runSmokeTests = async () => {
    if (!selectedMunicipalityId && userInfo?.role === 'ProviderAdmin') { toast.error('Bitte wählen Sie eine Gemeinde aus'); return; }
    setSmokeTestRunning(true); setSmokeTestResults(null);
    try {
      const results = [];
      for (const testConfig of SMOKE_TEST_QUERIES) {
        try {
          const payload = { message: testConfig.question, conversation_id: null };
          if (userInfo?.role === 'ProviderAdmin' && selectedMunicipalityId) payload.municipality_id = selectedMunicipalityId;
          const response = await apiClient.post('/ask_debug', payload);
          const citations = response.data?.citations || [];
          const answer = response.data?.answer || '';
          const latency_ms = response.data?.debug?.latency_ms || 0;
          const validation = validateCitations(citations, testConfig);
          results.push({ id: testConfig.id, name: testConfig.name, question: testConfig.question, passed: validation.passed, issues: validation.issues, answer, citations: validation.citations, latency_ms, intent: response.data?.debug?.intent || 'unknown', fallback_used: response.data?.debug?.used_fallback || false });
        } catch (error) {
          results.push({ id: testConfig.id, name: testConfig.name, question: testConfig.question, passed: false, issues: [`Error: ${error.response?.data?.detail || error.message || 'Unknown error'}`], answer: '', citations: [], latency_ms: 0, intent: 'unknown', fallback_used: false, error: true });
        }
      }
      setSmokeTestResults(results);
      const passedCount = results.filter(r => r.passed).length;
      toast.success(`Smoke Tests abgeschlossen: ${passedCount}/${results.length} bestanden`);
    } catch (error) {
      toast.error(`Fehler beim Ausführen der Smoke Tests: ${error.message}`);
      setSmokeTestResults([]);
    } finally { setSmokeTestRunning(false); }
  };

  // Restore running Test Suite job on mount
  useEffect(() => {
    if (!userInfo || userInfo.role !== 'ProviderAdmin') return;
    const savedJobId = sessionStorage.getItem('testSuiteJobId');
    if (!savedJobId) return;
    if (testSuiteJobId === savedJobId && testSuitePollIntervalRef.current) return;
    setTestSuiteJobId(savedJobId);
    setTestSuiteRunning(true);
    if (testSuitePollIntervalRef.current) { clearInterval(testSuitePollIntervalRef.current); testSuitePollIntervalRef.current = null; }
    pollTestSuiteStatus(savedJobId);
    const interval = setInterval(() => { pollTestSuiteStatus(savedJobId); }, 2000);
    testSuitePollIntervalRef.current = interval;
    return () => { clearInterval(interval); testSuitePollIntervalRef.current = null; };
  }, [userInfo?.role]);

  // Load reports on mount
  useEffect(() => {
    if (userInfo?.role === 'ProviderAdmin') loadTestSuiteReports();
  }, [userInfo?.role]);

  // Cleanup polling on unmount
  useEffect(() => { return () => { if (testSuitePollIntervalRef.current) { clearInterval(testSuitePollIntervalRef.current); testSuitePollIntervalRef.current = null; } }; }, []);

  return (
    <>
      <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Testumgebung</h2>
      {userInfo?.role === 'ProviderAdmin' && !selectedMunicipalityId && (
        <Alert className={isDark ? 'border-amber-700 bg-amber-950/50' : 'border-amber-200 bg-amber-50'}>
          <AlertDescription>Bitte oben eine Gemeinde wählen (Mandant filtern), um Testfragen zu senden.</AlertDescription>
        </Alert>
      )}
      
      {/* Test Suite Section (Provider Admin only) */}
      {userInfo?.role === 'ProviderAdmin' && (
        <Card className={`${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'} shadow-lg rounded-xl`}>
          <CardHeader className="pb-4">
            <CardTitle className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Test Suite Konfiguration</CardTitle>
            <CardDescription className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              Führt die vollständige Test-Suite aus: 240 verschiedene Fragen mit jeweils 4 Variationen (960 Prompts total).
              Optional: Redteam-Tests (40 × 4 = 160 Prompts) und Dialogues (30 Multi-Turn).
            </CardDescription>
            {!testSuiteRunning && (
              <div className={`text-sm mt-2 px-4 py-2 rounded-lg ${isDark ? 'bg-slate-800 text-slate-300' : 'bg-slate-100 text-slate-700'}`}>
                <span className="font-medium">Geschätzte Anzahl Tests: </span>
                <span className="font-bold">{estimateTestCount(testSuiteForm, INTENT_CATEGORIES)}</span>
                {testSuiteForm.limit && (<span className="text-xs ml-2">(Limit: {testSuiteForm.limit})</span>)}
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Suite Selection */}
            <div className="space-y-3">
              <Label className={`text-sm font-semibold ${isDark ? 'text-slate-400' : 'text-slate-700'}`}>Suite-Auswahl</Label>
              <RadioGroup value={testSuiteForm.suite} onValueChange={(value) => setTestSuiteForm({...testSuiteForm, suite: value})} disabled={testSuiteRunning} className="flex gap-6">
                <div className="flex items-center space-x-2"><RadioGroupItem value="full" id="suite-full" /><Label htmlFor="suite-full" className={`cursor-pointer ${isDark ? 'text-slate-400' : 'text-slate-700'}`}>Full Suite (~2h, 960+ tests)</Label></div>
                <div className="flex items-center space-x-2"><RadioGroupItem value="golden" id="suite-golden" /><Label htmlFor="suite-golden" className={`cursor-pointer ${isDark ? 'text-slate-400' : 'text-slate-700'}`}>Golden (16 tests, ~1min)</Label></div>
                <div className="flex items-center space-x-2"><RadioGroupItem value="smoke" id="suite-smoke" /><Label htmlFor="suite-smoke" className={`cursor-pointer ${isDark ? 'text-slate-400' : 'text-slate-700'}`}>Smoke Suite (~10-15min)</Label></div>
                <div className="flex items-center space-x-2"><RadioGroupItem value="fast" id="suite-fast" /><Label htmlFor="suite-fast" className={`cursor-pointer ${isDark ? 'text-slate-400' : 'text-slate-700'}`}>Fast Suite (~few min)</Label></div>
                <div className="flex items-center space-x-2"><RadioGroupItem value="super_judge_300" id="suite-sj300" /><Label htmlFor="suite-sj300" className={`cursor-pointer ${isDark ? 'text-slate-400' : 'text-slate-700'}`}>Super-Judge 300 (4 Tiers)</Label></div>
              </RadioGroup>
            </div>
            <Separator className={isDark ? 'bg-slate-700' : 'bg-slate-200'} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-6">
                {/* Intent Categories */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className={`text-sm font-semibold ${isDark ? 'text-slate-400' : 'text-slate-700'}`}>Intent-Kategorien</Label>
                    <div className="flex gap-2">
                      <Button type="button" variant="ghost" size="sm" onClick={() => setTestSuiteForm({...testSuiteForm, intent_tags: INTENT_CATEGORIES.map(c => c.value)})} disabled={testSuiteRunning} className={`text-xs h-7 px-2 ${isDark ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-800' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-100'}`}>Alle</Button>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setTestSuiteForm({...testSuiteForm, intent_tags: []})} disabled={testSuiteRunning} className={`text-xs h-7 px-2 ${isDark ? 'text-slate-200 hover:text-white hover:bg-slate-800' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-100'}`}>Keine</Button>
                    </div>
                  </div>
                  <div className={`flex flex-wrap gap-2 p-4 rounded-lg border min-h-[140px] max-h-[200px] overflow-y-auto ${isDark ? 'border-slate-700 bg-slate-950/50' : 'border-slate-300 bg-slate-50'}`}>
                    {INTENT_CATEGORIES.map(cat => {
                      const isSelected = testSuiteForm.intent_tags.includes(cat.value);
                      return (
                        <Badge key={cat.value} variant={isSelected ? "default" : "outline"}
                          className={`cursor-pointer transition-all px-3 py-1.5 text-sm ${isSelected ? (isDark ? 'bg-sky-600 text-white border-sky-500 hover:bg-sky-500' : 'bg-sky-600 text-white border-sky-500 hover:bg-sky-500') : (isDark ? 'bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-700 hover:border-slate-600' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100 hover:border-slate-400')}`}
                          onClick={() => {
                            if (testSuiteRunning) return;
                            if (isSelected) setTestSuiteForm({...testSuiteForm, intent_tags: testSuiteForm.intent_tags.filter(t => t !== cat.value)});
                            else setTestSuiteForm({...testSuiteForm, intent_tags: [...testSuiteForm.intent_tags, cat.value]});
                          }}>{cat.label}</Badge>
                      );
                    })}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className={`text-sm font-semibold ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>Limit (optional)</Label>
                  <Input type="number" min="1" placeholder="Alle" value={testSuiteForm.limit || ''} onChange={e => setTestSuiteForm({...testSuiteForm, limit: e.target.value ? parseInt(e.target.value) : null})} disabled={testSuiteRunning} className={`${isDark ? 'bg-slate-950 border-slate-700 text-white' : 'bg-white border-slate-300'} rounded-lg`} />
                </div>
              </div>
              <div className="space-y-6">
                {/* Suite Filters */}
                <div className={`p-4 rounded-lg border ${isDark ? 'border-slate-700 bg-slate-950/50' : 'border-slate-300 bg-slate-50'}`}>
                  <Label className={`text-sm font-semibold mb-3 block ${isDark ? 'text-white' : 'text-slate-700'}`}>Suite-Filter</Label>
                  <div className="space-y-3">
                    {SUITE_TAGS.map(tag => (
                      <div key={tag.value} className="flex items-center justify-between">
                        <Label htmlFor={`suite-tag-${tag.value}`} className={`cursor-pointer font-medium ${isDark ? 'text-white' : 'text-slate-700'}`}>{tag.label}</Label>
                        <div className={isDark ? "[&>button[data-state=checked]]:bg-sky-600 [&>button[data-state=unchecked]]:bg-slate-600 [&>button[data-state=unchecked]]:border-slate-500 [&>button>span]:bg-white" : "[&>button[data-state=checked]]:bg-sky-600 [&>button[data-state=unchecked]]:bg-slate-300 [&>button[data-state=unchecked]]:border-slate-400 [&>button>span]:bg-white"}>
                          <Switch id={`suite-tag-${tag.value}`} checked={testSuiteForm.suite_tags.includes(tag.value)} onCheckedChange={(checked) => { if (checked) setTestSuiteForm({...testSuiteForm, suite_tags: [...testSuiteForm.suite_tags, tag.value]}); else setTestSuiteForm({...testSuiteForm, suite_tags: testSuiteForm.suite_tags.filter(t => t !== tag.value)}); }} disabled={testSuiteRunning} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Options */}
                <div className={`p-4 rounded-lg border ${isDark ? 'border-slate-700 bg-slate-950/50' : 'border-slate-300 bg-slate-50'}`}>
                  <Label className={`text-sm font-semibold mb-3 block ${isDark ? 'text-white' : 'text-slate-700'}`}>Optionen</Label>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="option-redteam" className={`cursor-pointer font-medium ${isDark ? 'text-white' : 'text-slate-700'}`}>Redteam-Tests</Label>
                      <div className={isDark ? "[&>button[data-state=checked]]:bg-sky-600 [&>button[data-state=unchecked]]:bg-slate-600 [&>button[data-state=unchecked]]:border-slate-500 [&>button>span]:bg-white" : "[&>button[data-state=checked]]:bg-sky-600 [&>button[data-state=unchecked]]:bg-slate-300 [&>button[data-state=unchecked]]:border-slate-400 [&>button>span]:bg-white"}>
                        <Switch id="option-redteam" checked={testSuiteForm.include_redteam} onCheckedChange={(checked) => setTestSuiteForm({...testSuiteForm, include_redteam: checked})} disabled={testSuiteRunning} />
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="option-dialogues" className={`cursor-pointer font-medium ${isDark ? 'text-white' : 'text-slate-700'}`}>Dialogues</Label>
                      <div className={isDark ? "[&>button[data-state=checked]]:bg-sky-600 [&>button[data-state=unchecked]]:bg-slate-600 [&>button[data-state=unchecked]]:border-slate-500 [&>button>span]:bg-white" : "[&>button[data-state=checked]]:bg-sky-600 [&>button[data-state=unchecked]]:bg-slate-300 [&>button[data-state=unchecked]]:border-slate-400 [&>button>span]:bg-white"}>
                        <Switch id="option-dialogues" checked={testSuiteForm.include_dialogues} onCheckedChange={(checked) => setTestSuiteForm({...testSuiteForm, include_dialogues: checked})} disabled={testSuiteRunning} />
                      </div>
                    </div>
                  </div>
                </div>
                {/* Action Buttons */}
                <div className="flex flex-col gap-2 pt-4">
                  {testSuiteRunning ? (
                    <Button onClick={stopTestSuite} variant="outline" className={`rounded-lg ${isDark ? 'border-red-700 text-red-400 hover:bg-red-950' : 'border-red-300 text-red-600 hover:bg-red-50'}`} size="lg">
                      <X className="w-4 h-4 mr-2" /> Abbrechen & Aufräumen
                    </Button>
                  ) : (
                    <Button onClick={() => { if (testSuiteStatus?.status === 'completed' || testSuiteStatus?.status === 'failed' || testSuiteStatus?.status === 'cancelled') setTestSuiteStatus(null); startTestSuite(); }} disabled={!selectedMunicipalityId} className={`bg-sky-600 text-white hover:bg-sky-500 rounded-lg shadow-md ${isDark ? 'shadow-sky-900/50' : 'shadow-sky-200'} transition-all`} size="lg">
                      <Database className="w-4 h-4 mr-2" /> {testSuiteStatus?.status === 'completed' || testSuiteStatus?.status === 'failed' || testSuiteStatus?.status === 'cancelled' ? 'Neue Test-Suite starten' : 'Test-Suite starten'}
                    </Button>
                  )}
                  {(testSuiteStatus?.status === 'completed' || testSuiteStatus?.status === 'cancelled') && testSuiteStatus?.report_paths && (
                    <div className="flex gap-2">
                      <Button onClick={() => downloadReport(testSuiteStatus.report_paths.csv.split('/').pop())} variant="outline" className={`flex-1 rounded-lg ${isDark ? 'border-slate-700 text-slate-200 hover:bg-slate-800' : 'border-slate-300 text-slate-700 hover:bg-slate-100'}`}><Download className="w-4 h-4 mr-2" /> CSV</Button>
                      <Button onClick={() => downloadReport(testSuiteStatus.report_paths.json.split('/').pop())} variant="outline" className={`flex-1 rounded-lg ${isDark ? 'border-slate-700 text-slate-200 hover:bg-slate-800' : 'border-slate-300 text-slate-700 hover:bg-slate-100'}`}><Download className="w-4 h-4 mr-2" /> JSON</Button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {testSuiteJobId && (
              <div className={`flex items-center gap-2 flex-wrap rounded-md border px-3 py-2 ${isDark ? 'bg-slate-950/50 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                <Label className={`text-sm font-medium shrink-0 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Job-ID</Label>
                <code className={`text-sm font-mono truncate min-w-0 flex-1 ${isDark ? 'text-slate-200' : 'text-slate-800'}`} title={testSuiteJobId}>{testSuiteJobId}</code>
                <Button type="button" variant="ghost" size="sm" className="shrink-0 h-8 px-2 text-xs" onClick={() => copyToClipboard(testSuiteJobId, 'Job-ID')}><Copy className="w-3.5 h-3.5 mr-1" /> Kopieren</Button>
              </div>
            )}

            {testSuiteStatus && (
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Status: {testSuiteStatus.status === 'running' ? 'Läuft...' : testSuiteStatus.status === 'completed' ? 'Abgeschlossen' : 'Fehlgeschlagen'}</span>
                  <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{testSuiteStatus.completed_tests} / {testSuiteStatus.total_tests} Tests</span>
                </div>
                <div className={`h-2 rounded-full overflow-hidden ${isDark ? 'bg-slate-800' : 'bg-slate-200'}`}>
                  <div className={`h-full transition-all ${testSuiteStatus.status === 'completed' ? 'bg-green-500' : testSuiteStatus.status === 'failed' ? 'bg-red-500' : 'bg-sky-500'}`} style={{ width: `${testSuiteStatus.progress * 100}%` }} />
                </div>
                {testSuiteStatus.status === 'running' && testSuiteStatus.current_test && (<p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Aktuell: {testSuiteStatus.current_test}</p>)}
                {testSuiteStatus.status === 'cancelled' && (
                  <div className={`text-sm mt-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                    <p className="font-semibold text-orange-600">Abgebrochen</p>
                    <p>Abgeschlossen: {testSuiteStatus.completed_tests} / {testSuiteStatus.total_tests}</p>
                    {testSuiteStatus.report_paths && (<p className="text-xs mt-1">Partielle Reports wurden gespeichert</p>)}
                  </div>
                )}
                {testSuiteStatus.status === 'completed' && (
                  <div className={`text-sm mt-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                    <p>Bestanden: <span className="font-semibold text-green-600">{testSuiteStatus.passed_tests}</span></p>
                    <p>Fehlgeschlagen: <span className="font-semibold text-red-600">{testSuiteStatus.failed_tests}</span></p>
                    <p>Pass-Rate: <span className="font-semibold">{testSuiteStatus.total_tests > 0 ? ((testSuiteStatus.passed_tests / testSuiteStatus.total_tests) * 100).toFixed(1) : 0}%</span></p>
                  </div>
                )}
                {testSuiteStatus.error && (<Alert className={isDark ? 'border-red-700 bg-red-950/50' : 'border-red-200 bg-red-50'}><AlertDescription className={isDark ? 'text-red-300' : 'text-red-700'}>Fehler: {testSuiteStatus.error}</AlertDescription></Alert>)}
              </div>
            )}

            {testSuiteStatus?.results && testSuiteStatus.results.length > 0 && (
              <div className="mt-4">
                <Label className={`text-sm font-medium mb-2 block ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Ergebnisse (erste 20) - Answer vs Links getrennt evaluiert</Label>
                <ScrollArea className={`h-[400px] rounded border p-2 ${isDark ? 'bg-slate-950 border-slate-700' : 'bg-white border-slate-200'}`}>
                  <div className="space-y-2">
                    {testSuiteStatus.results.slice(0, 20).map((result, idx) => {
                      const passed = result.overall_passed !== undefined ? result.overall_passed : result.base_ok;
                      const answerPassed = result.answer_eval?.passed;
                      const linkPassed = result.link_eval?.passed;
                      const failureBucket = result.failure_bucket || 'none';
                      return (
                        <div key={idx} className={`p-2 rounded border ${passed ? (isDark ? 'border-green-700 bg-green-950/20' : 'border-green-200 bg-green-50') : (isDark ? 'border-red-700 bg-red-950/20' : 'border-red-200 bg-red-50')}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant={passed ? 'default' : 'destructive'} className="text-xs">{passed ? 'PASS' : 'FAIL'}</Badge>
                            {failureBucket !== 'none' && (<Badge variant="outline" className="text-xs">{failureBucket === 'answer' ? 'Answer' : failureBucket === 'links' ? 'Links' : 'Both'}</Badge>)}
                            <span className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{result.test_id || result.base_id}: {result.question?.substring(0, 50) || result.label || '—'}</span>
                          </div>
                          <div className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Answer: {answerPassed ? '✓' : '✗'} | Links: {linkPassed ? '✓' : '✗'} | Intent: {result.normalized_intent || result.actual_intent || '—'}</div>
                          {!passed && (
                            <div className="mt-1 text-xs">
                              {result.answer_eval?.reasons?.length > 0 && (<div className={isDark ? 'text-red-400' : 'text-red-600'}>Answer: {result.answer_eval.reasons.join('; ')}</div>)}
                              {result.link_eval?.reasons?.length > 0 && (<div className={isDark ? 'text-red-400' : 'text-red-600'}>Links: {result.link_eval.reasons.join('; ')}</div>)}
                              {result.variants?.some(v => !v.ok) && (<div>{result.variants.filter(v => !v.ok).map((v, vi) => (<div key={vi} className={isDark ? 'text-red-400' : 'text-red-600'}>Variant {v.variant}: {v.reasons.join('; ')}</div>))}</div>)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </div>
            )}

            {/* Available Reports */}
            {testSuiteReports.length > 0 && (
              <div className="mt-6">
                <Label className={`text-sm font-medium mb-2 block ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Verfügbare Reports ({testSuiteReports.length})</Label>
                <div className={`rounded border p-3 ${isDark ? 'bg-slate-950 border-slate-700' : 'bg-white border-slate-200'}`}>
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {testSuiteReports.map((report, idx) => (
                      <div key={idx} className={`flex items-center justify-between p-2 rounded ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}>
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{report.suite} - {report.timestamp}</div>
                          <div className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{report.filename_json}</div>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          {report.filename_csv && (<Button size="sm" variant="outline" onClick={() => downloadReport(report.filename_csv)} className={isDark ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : ''}><Download className="w-3 h-3 mr-1" /> CSV</Button>)}
                          <Button size="sm" variant="outline" onClick={() => downloadReport(report.filename_json)} className={isDark ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : ''}><Download className="w-3 h-3 mr-1" /> JSON</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Smoke Test Section */}
      {userInfo?.role === 'ProviderAdmin' && (
        <Card className={isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}>
          <CardHeader>
            <CardTitle className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Smoke Test (Citations)</CardTitle>
            <CardDescription className={isDark ? 'text-slate-400' : 'text-slate-500'}>Schnelle Tests zur Überprüfung der Citation-Qualität: 3-5 kanonische Abfragen, die prüfen, ob die richtigen Seiten zitiert werden.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Button onClick={runSmokeTests} disabled={smokeTestRunning || (!selectedMunicipalityId && userInfo?.role === 'ProviderAdmin')} className="bg-orange-600 text-white hover:bg-orange-500">
                {smokeTestRunning ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Läuft...</>) : (<><Flame className="w-4 h-4 mr-2" /> Run Smoke Tests</>)}
              </Button>
            </div>
            {smokeTestResults && (
              <div className="space-y-3">
                <div className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Ergebnisse: {smokeTestResults.filter(r => r.passed).length} / {smokeTestResults.length} bestanden</div>
                {smokeTestResults.map((result) => {
                  const isExpanded = smokeTestExpanded[result.id] || false;
                  return (
                    <div key={result.id} className={`p-3 rounded border ${result.passed ? (isDark ? 'border-green-700 bg-green-950/20' : 'border-green-200 bg-green-50') : (isDark ? 'border-red-700 bg-red-950/20' : 'border-red-200 bg-red-50')}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant={result.passed ? 'default' : 'destructive'} className="text-xs">{result.passed ? 'PASS' : 'FAIL'}</Badge>
                            <span className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{result.name}</span>
                            {result.latency_ms > 0 && (<span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>({result.latency_ms}ms)</span>)}
                          </div>
                          <div className={`text-xs mb-2 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{result.question}</div>
                          {!result.passed && result.issues && result.issues.length > 0 && (<div className="mb-2">{result.issues.map((issue, idx) => (<div key={idx} className={`text-xs ${isDark ? 'text-red-400' : 'text-red-600'}`}>⚠ {issue}</div>))}</div>)}
                          {result.citations && result.citations.length > 0 && (
                            <div className="mb-2">
                              <div className={`text-xs font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Citations ({result.citations.length}):</div>
                              <ul className={`text-xs space-y-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                                {result.citations.map((citation, idx) => (<li key={idx} className="flex items-start gap-1"><span>•</span><span className="flex-1 break-all">{citation.title && <span className="font-medium">{citation.title}: </span>}<code className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{citation.url}</code></span></li>))}
                              </ul>
                            </div>
                          )}
                          {result.answer && (
                            <div className="mt-2">
                              <Button variant="ghost" size="sm" onClick={() => setSmokeTestExpanded(prev => ({ ...prev, [result.id]: !isExpanded }))} className={`h-6 px-2 text-xs ${isDark ? 'text-slate-400 hover:text-slate-300' : 'text-slate-600 hover:text-slate-700'}`}>
                                {isExpanded ? (<><ChevronUp className="w-3 h-3 mr-1" /> Answer verbergen</>) : (<><ChevronDown className="w-3 h-3 mr-1" /> Answer anzeigen</>)}
                              </Button>
                              {isExpanded && (<div className={`mt-2 p-2 rounded text-xs ${isDark ? 'bg-slate-950 border border-slate-700 text-slate-300' : 'bg-slate-50 border border-slate-200 text-slate-700'}`}>{result.answer}</div>)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {smokeTestRunning && !smokeTestResults && (<div className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Führe Smoke Tests aus...</div>)}
          </CardContent>
        </Card>
      )}

      {/* Test Cases + Chat + Debug */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-4">
          <Card className={isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}>
            <CardHeader className="pb-2">
              <CardTitle className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Testfälle</CardTitle>
              <CardDescription className={isDark ? 'text-slate-400' : 'text-slate-500'}>Klicken Sie „Senden", um die Frage an die{' '}
                {(() => {
                  if (userInfo?.role === 'ProviderAdmin' && selectedMunicipalityId) { const muni = municipalitiesList.find(m => m.id === selectedMunicipalityId); return `\u201E${muni?.name || selectedMunicipalityId}\u201C`; }
                  if (userInfo?.municipality_id) { const muni = municipalitiesList.find(m => m.id === userInfo.municipality_id); return `\u201E${muni?.name || userInfo.municipality_id}\u201C`; }
                  return '';
                })()}{' '} KI zu schicken.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {TEST_CASES.map((q, i) => (
                <div key={`builtin-${i}`} className="flex items-center gap-2">
                  <span className={`flex-1 text-sm truncate ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{q}</span>
                  <Button size="sm" variant="outline" disabled={testEnvLoading || !testEnvCanSend} onClick={() => sendTestMessage(q)} className="shrink-0">Senden</Button>
                </div>
              ))}
              {customTestCases.length > 0 && (
                <div className={`border-t pt-2 mt-2 ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                  <p className={`text-xs font-medium mb-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Eigene Testfragen</p>
                  {customTestCases.map((q, i) => (
                    <div key={`custom-${i}`} className="flex items-center gap-2 mb-1">
                      <span className={`flex-1 text-sm truncate ${isDark ? 'text-sky-300' : 'text-sky-700'}`}>{q}</span>
                      <Button size="sm" variant="outline" disabled={testEnvLoading || !testEnvCanSend} onClick={() => sendTestMessage(q)} className="shrink-0">Senden</Button>
                      <Button size="sm" variant="ghost" onClick={() => removeCustomTestCase(i)} className={`shrink-0 h-8 w-8 p-0 ${isDark ? 'text-slate-500 hover:text-red-400 hover:bg-slate-800' : 'text-slate-400 hover:text-red-500 hover:bg-slate-100'}`} title="Testfrage entfernen"><X className="w-3.5 h-3.5" /></Button>
                    </div>
                  ))}
                </div>
              )}
              <div className={`border-t pt-2 mt-2 ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                <form onSubmit={(e) => { e.preventDefault(); addCustomTestCase(newTestCaseInput); }} className="flex items-center gap-2">
                  <Input value={newTestCaseInput} onChange={(e) => setNewTestCaseInput(e.target.value)} placeholder="Eigene Testfrage hinzufügen…" className={`flex-1 h-8 text-sm ${isDark ? 'bg-slate-950 border-slate-700 text-white placeholder:text-slate-500' : ''}`} />
                  <Button type="submit" size="sm" variant="outline" disabled={!newTestCaseInput.trim()} className="shrink-0"><Plus className="w-3.5 h-3.5 mr-1" /> Hinzufügen</Button>
                </form>
              </div>
            </CardContent>
          </Card>
          <Card className={isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}>
            <CardHeader className="pb-2"><CardTitle className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Chat</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <form onSubmit={(e) => { e.preventDefault(); sendTestMessage(testEnvInput); }} className="flex gap-2">
                <Input value={testEnvInput} onChange={e => setTestEnvInput(e.target.value)} placeholder="Frage eingeben…" disabled={testEnvLoading} className={isDark ? 'bg-slate-950 border-slate-700 text-white' : ''} />
                <Button type="submit" disabled={testEnvLoading || !testEnvInput.trim() || !testEnvCanSend}>{testEnvLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</Button>
              </form>
              <ScrollArea className="h-[200px] rounded border p-2">
                {testEnvMessages.map((m, i) => (
                  <div key={i} className={`text-sm mb-2 ${m.role === 'user' ? (isDark ? 'text-sky-300' : 'text-sky-700') : (isDark ? 'text-slate-300' : 'text-slate-700')}`}>
                    <span className="font-medium">{m.role === 'user' ? 'Sie' : 'Assistent'}: </span>{typeof m.content === 'string' ? m.content : String(m.content ?? '')}
                  </div>
                ))}
              </ScrollArea>
              {lastQuestionForFeedback && (
                <div className={`border-t pt-3 mt-3 ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                  <p className={`text-xs font-medium mb-2 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Feedback zur letzten Antwort</p>
                  <div className="flex gap-2 items-center flex-wrap">
                    <Button type="button" size="sm" variant={feedbackHelpful === true ? 'default' : 'outline'} onClick={() => setFeedbackHelpful(true)}><ThumbsUp className="w-4 h-4 mr-1" /> Hilfreich</Button>
                    <Button type="button" size="sm" variant={feedbackHelpful === false ? 'default' : 'outline'} onClick={() => setFeedbackHelpful(false)}><ThumbsDown className="w-4 h-4 mr-1" /> Nicht hilfreich</Button>
                    <Select value={feedbackReason || 'none'} onValueChange={setFeedbackReason}>
                      <SelectTrigger className={`w-[180px] ${isDark ? 'bg-slate-950 border-slate-700' : ''}`}><SelectValue placeholder="Grund (optional)" /></SelectTrigger>
                      <SelectContent className={isDark ? 'bg-slate-900 border-slate-700' : ''}><SelectItem value="none">—</SelectItem><SelectItem value="unvollständig">Unvollständig</SelectItem><SelectItem value="falsch">Falsch</SelectItem><SelectItem value="nicht_relevant">Nicht relevant</SelectItem><SelectItem value="sonstiges">Sonstiges</SelectItem></SelectContent>
                    </Select>
                    <Input placeholder="Kommentar (optional)" value={feedbackComment} onChange={e => setFeedbackComment(e.target.value)} className={`flex-1 min-w-[120px] ${isDark ? 'bg-slate-950 border-slate-700' : ''}`} />
                    <Button size="sm" onClick={submitTestFeedback} disabled={feedbackHelpful === null || feedbackSending} className="bg-sky-600 text-white hover:bg-sky-500">{feedbackSending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Feedback senden'}</Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        {lastDebugResponse && (
          <Card className={isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}>
            <CardHeader className="pb-2"><CardTitle className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Debug-Informationen</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                <p className="font-medium mb-1">Intent &amp; Metadaten</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>Intent: {lastDebugResponse?.debug?.intent ?? '—'}</li>
                  <li>Confidence: {lastDebugResponse?.confidence ?? '—'}</li>
                  <li>Latency: {lastDebugResponse?.debug?.latency_ms ?? '—'} ms</li>
                  <li>Citations: {lastDebugResponse?.debug?.citations_count ?? lastDebugResponse?.citations?.length ?? 0}</li>
                  {lastDebugResponse?.debug?.cache_hit != null && (<li>Cache: {lastDebugResponse.debug.cache_hit ? 'Hit' : 'Miss'}</li>)}
                  {lastDebugResponse?.debug?.used_fallback != null && (<li>Fallback: {lastDebugResponse.debug.used_fallback ? 'Ja' : 'Nein'}</li>)}
                </ul>
              </div>
              <div className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                <p className="font-medium mb-1">Quellen</p>
                {lastDebugResponse?.citations?.length ? (
                  <ul className="space-y-1">{lastDebugResponse.citations.map((c, i) => (<li key={i}><a href={c.url} target="_blank" rel="noopener noreferrer" className={isDark ? 'text-sky-400 hover:underline' : 'text-sky-600 hover:underline'}>{c.title || c.url || `Quelle ${i + 1}`}</a></li>))}</ul>
                ) : (<span className="italic">Keine Quellen</span>)}
              </div>
              <div className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                <p className="font-medium mb-1">Retrieval Debug (top_k)</p>
                {lastDebugResponse?.debug?.retrieval?.score_gaps?.length ? (<p className={`text-xs mb-1 ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>Score gaps (delta): {lastDebugResponse.debug.retrieval.score_gaps.join(', ')}</p>) : null}
                {lastDebugResponse?.debug?.retrieval?.top_k?.length ? (
                  <ul className="space-y-1">{lastDebugResponse.debug.retrieval.top_k.map((r, i) => (<li key={i}><a href={r.url} target="_blank" rel="noopener noreferrer" className={isDark ? 'text-sky-400 hover:underline' : 'text-sky-600 hover:underline'}>{r.title || r.url || r.doc_id || `#${i + 1}`}</a>{' '}<span className={isDark ? 'text-slate-500' : 'text-slate-500'}>(vector: {r.vector_score ?? r.score}{r.post_rerank_score != null ? ` → rerank: ${r.post_rerank_score}` : ''}{r.score_gap != null ? ` Δ${r.score_gap}` : ''}{r.evidence === true ? ' ✓Evidence' : r.evidence === false ? '' : ''})</span></li>))}</ul>
                ) : (<span className="italic">Keine Kandidaten</span>)}
              </div>
            </CardContent>
          </Card>
        )}
        <Card className={isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}>
          <CardHeader className="pb-2"><CardTitle className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Letzte Antwort (JSON)</CardTitle></CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px] rounded border p-2">
              <pre className={`text-xs whitespace-pre-wrap break-words ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{typeof lastResponseJson === 'string' ? lastResponseJson : '— Keine Antwort yet. Senden Sie eine Testfrage.'}</pre>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
